/* DeviceMultiEditViewRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const Grid = require("@gluenetworks/steve_ui_widgets").GridGl.Grid;
const FormsComponent = require("@gluenetworks/forms").ui.multiEditComponent;
const BaseRefResolver = require("@gluenetworks/forms").resolvers.ref.base;
const InheritedFieldUtility = require("../../../../lib/InheritedFieldUtility");
const moment = require("moment");
require("moment-duration-format");

const readOnlyFieldIds = [
    "name",
    "ip",
    "latestHaveIChangedLastChecked",
    "latestHaveIChanged",
    "capturedBy",
    "discoveredOn",
    "discoveryStatus",
    "discoveredBy",
    "detectedVendor",
    "detectedHostName",
    "detectedOs",
    "detectedSoftwareVersion",
    "detectedSerialNumber",
    "detectedSku",
    "detectedUptime",
    "elementCount",
    "configuredBy",
    "configuredInfo",
    "configuredOn",
    "configuredType",
    "updatedOn",
    "updatedBy",
    "lastActivityOn",
    "lastActivityBy",
    "lastActivityType",
    "lastActivityError",
    "fileServerIdentifier",
    "fileServerName",
    "haPairName",
    "osPlanAction",
    "osManagementActionOn",
    "osManagementActionError",
    "osPlanName",
    "osPlanAction",
    "osManagementActionBy",
    "lastCmdbSyncOn",
];

class DeviceMultiEditViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("device-multiedit-view-gl");
        this._rootElem.component = this._model;

        this._detailSection = document.createElement("div");
        this._detailSection.classList.add("form-section");

        this._selectedDevicesSection = document.createElement("div");
        this._selectedDevicesSection.classList.add("selected-device-view-gl", "hidden");

        this._logger = this._model.logger;

        this._refResolver = new BaseRefResolver(model._nibApiService, model._userSession, true);
        this._formsComponent = new FormsComponent(this._refResolver, undefined, false);
        this._viewButtonContainer = document.createElement("view-button-container");
        this._viewAllButton = new ButtonControl({
            label: "View All",
            darkTheme: false,
        });
        this._viewButtonContainer.appendChild(this._viewAllButton.renderer.rootElement);
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveDeviceDetails",
            handler: (this._onDeviceDetailsSave = this._onDeviceDetailsSave.bind(this)),
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelDeviceDetails",
            handler: (this._onDeviceDetailsCancel = this._onDeviceDetailsCancel.bind(this)),
        });
        this._siteCfUtility = this._model._siteCfUtility;

        let columnConfig = {
            name: {
                label: "Selected Devices",
                width: 0.2,
            },
            description: {
                label: "Description",
                width: 0.8,
            },
        };

        this._selectListGrid = new Grid({
            columns: columnConfig,
            showHeader: true,
            columnOrder: ["name", "description"],
        });
        this._gridRows = {};

        this._obsConfig = {attributes: true, childList: false, subtree: true};

        this._ObsCallback = (mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.target.tagName === "INPUT-POWERBUTTON-CONTROL") {
                    this._onMultiEditActive();
                }
            }
        };

        this._muObserver = new MutationObserver(this._ObsCallback);

        this._pristineConnectionObject;
        this._renderingForm = false;
        this._toggleActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "EditorModeChanged",
            (this._onEditorModeChanged = this._onEditorModeChanged.bind(this))
        );
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on(
            "OsImageServerFeedUpdated",
            (this._onOsImageServerFeedUpdated = this._onOsImageServerFeedUpdated.bind(this))
        );
        this._viewAllButton.on(
            "UserClick",
            (this._onViewButtonClick = this._onViewButtonClick.bind(this))
        );
        this._model.on(
            "EngineZoneFeedUpdate",
            (this._onEngineZoneFeedUpdated = this._onEngineZoneFeedUpdated.bind(this))
        );
        this._model.on(
            "OrgZoneFeedUpdate",
            (this._onOrgZoneFeedUpdated = this._onOrgZoneFeedUpdated.bind(this))
        );

        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );

        this._model.on(
            "SiteFeedUpdated",
            (this._onSiteFeedUpdated = this._onSiteFeedUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("EditorModeChanged", this._onEditorModeChanged);
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("OsImageServerFeedUpdated", this._onOsImageServerFeedUpdated);
        this._model.removeListener("EngineZoneFeedUpdate", this._onEngineZoneFeedUpdated);
        this._model.removeListener("OrgZoneFeedUpdate", this._onOrgZoneFeedUpdated);
        this._viewAllButton.removeListener("UserClick", this._onViewButtonClick);
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._model.removeListener("SiteFeedUpdated", this._onSiteFeedUpdated);
    }

    _registerFormsApiListener() {
        this._formsComponent.renderer._formRendererApi.on(
            "MultiEditActive",
            (this._onMultiEditActive = this._onMultiEditActive.bind(this))
        );
    }

    _unregisterFormsApiListener() {
        this._formsComponent.renderer._formRendererApi.removeListener(
            "MultiEditActive",
            this._onMultiEditActive
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        // this._unregisterFormsApiListener();
        this._muObserver.disconnect();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._viewAllButton = undefined;
                this._viewButtonContainer.remove();
                this._viewButtonContainer = undefined;
                this._detailSection.remove();
                this._detailSection = undefined;
                this._selectedDevicesSection.remove();
                this._selectedDevicesSection = undefined;
                this._selectListGrid.shutdown().then(() => {
                    this._selectListGrid = undefined;
                });
                this._pristineConnectionObject = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "DeviceDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => this._model.whenModelIsReady())
            .then(() => {
                this._renderDeviceDetails();
                this._renderGridRows();
                if (!this._rootElem.hasChildNodes()) {
                    this._detailSection.appendChild(this._formsComponent.renderer.rootElement);
                    this._muObserver.observe(
                        this._formsComponent.renderer.rootElement,
                        this._obsConfig
                    );
                    this._detailSection.appendChild(this._viewButtonContainer);
                    this._rootElem.appendChild(this._detailSection);
                    this._rootElem.appendChild(this._selectedDevicesSection);
                }
            })
            .then(() => this._hideModal())
            .catch((err) => {
                if (err === "devices are no longer exist") {
                    return this.model.gameEditor.closeEditor();
                } else {
                    return this._showError(err);
                }
            })
            .then(() => this._toggleActions());
    }

    _onEditorModeChanged() {
        return Promise.resolve().then(() => {
            if (this._model.editorMode === "viewall") {
                this._renderGridRows();
                this._detailSection.classList.add("hidden");
                this._selectedDevicesSection.classList.remove("hidden");
                this._renderBackButton();
            } else {
                this._detailSection.classList.remove("hidden");
                this._selectedDevicesSection.classList.add("hidden");
            }
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        this._model.gameEditor.showViewAction("SaveDeviceDetails");
        this._model.gameEditor.showViewAction("CancelDeviceDetails");
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveDeviceDetails");
        this._model.gameEditor.hideViewAction("CancelDeviceDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveDeviceDetails");
        this._model.gameEditor.disableViewAction("CancelDeviceDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveDeviceDetails");
        this._model.gameEditor.enableViewAction("CancelDeviceDetails");
    }

    _toggleActions() {
        if (this._model.isDirty()) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderDeviceDetails() {
        this._model.setTabDirty(false);
        this._model._tabComponent.setTitle("Multi-Edit (" + this._model._devices.length + ")");

        return Promise.resolve()
            .then(
                () => {
                    let jtype = this._getJtype();
                    let pristineValues = this._getPristineValues();
                    this._formsComponent.setTypeInformation(jtype, true);
                    this._formsComponent.setPristineValue(pristineValues, true);

                    this._renderingForm = true;
                    return this._formsComponent.renderer.render().then(() => {
                        this._renderingForm = false;
                        // this._registerFormsApiListener();
                        if (this._model._lockedConnectionMethods.length === 1) {
                            const connectionObjDropdown = document.querySelector(
                                ".connection_object .select_options_header .chosen-container[title='Connection Method']"
                            );
                            if (connectionObjDropdown) {
                                connectionObjDropdown.classList.add("disabled");
                            }
                        }

                        this._setReadOnly();
                        this._siteCfIconsList = this._siteCfUtility.generateInheritanceIconsME(
                            this._rootElem,
                            this._model._inheritableCfs
                        );

                        if (this._model._deviceCategory !== "iot") {
                            this._discoveryLevelInheritanceUtility = new InheritedFieldUtility(
                                "discoveryLevel",
                                "discovery_level",
                                undefined,
                                undefined,
                                this._formsComponent,
                                undefined,
                                undefined,
                                undefined
                            );
                            this._discoveryLevelInheritanceUtility.generateIconMe();
                        }
                    });
                },
                (err) => {
                    this._logger.warn(
                        {error: err},
                        "DeviceDetailsViewRenderer._renderDeviceDetails() error."
                    );
                }
            )
            .then(() => {
                this._enableViewActions();
            });
    }
    _renderGridRows() {
        this._gridRows = {};
        let data = this._model._devices;
        data.forEach((obj) => {
            this._gridRows[obj.id] = {
                name: obj.name,
                description: obj.description,
            };
        });
        this._selectListGrid.setRows(this._gridRows);
        this._selectedDevicesSection.appendChild(this._selectListGrid.renderer.rootElement);
    }
    _showLoader(options) {
        return this._model._modalUserFeedback.displayLoading(options);
    }

    _showError(error) {
        return this._model._modalUserFeedback.displayError(error);
    }

    _hideModal() {
        return this._model._modalUserFeedback.hideDisplayed();
    }

    _onViewButtonClick() {
        const selectedDevice = this._model._devices;
        if (selectedDevice.length > 0) {
            this._model.setEditorMode("viewall");
        } else {
            this._model._logger.warn(
                {},
                "User clicked on viewAll in the multiEditView does not have a Selected Device."
            );
        }
    }
    _renderBackButton() {
        const model = this._model;
        const bcs = [
            {
                content: backButton,
                left: {
                    arrow: {
                        direction: "left",
                    },
                },
            },
        ];
        this._model.gameEditor.setBreadCrumbs(bcs);

        return Promise.resolve();

        function backButton() {
            const el = document.createElement("span");
            el.innerHTML = "Multi-Edit";
            el.addEventListener("click", () => {
                // clear breadcrumbs link
                model.gameEditor.setBreadCrumbs([]);
                // change tab back to general
                model.setEditorMode("general");
            });
            // prettier-ignore
            this.renderer = { //jshint ignore:line
                rootElement: el
            };
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onMultiEditActive() {
        const iconEls = this._rootElem.querySelectorAll(".cf-hierarchy base-icon-gl");
        this._siteCfUtility.removeInheritIconListenersME(iconEls);
        if (this._model._deviceCategory !== "iot") {
            this._discoveryLevelInheritanceUtility.removeEventListenerMe();
        }
        iconEls.forEach((el) => {
            el.classList.remove("enabled");
        });
        const formValues = this._formsComponent.renderer._formRendererApi.getValue();
        if (formValues && formValues.customFieldValues) {
            Object.keys(formValues.customFieldValues).forEach((key) => {
                if (this._model._inheritableCfs.indexOf(key) >= 0) {
                    const cfEl = this._rootElem.querySelector(
                        "." + CSS.escape(key.replaceAll("-", "_"))
                    );
                    if (!cfEl) {
                        return;
                    }
                    const iconEl = cfEl.querySelector(".cf-hierarchy base-icon-gl");
                    iconEl.classList.add("enabled");
                    this._siteCfUtility.addInheritIconListenerME(iconEl);
                }
            });
        }
        if (formValues && formValues.discoveryLevel && this._model._deviceCategory !== "iot") {
            this._discoveryLevelInheritanceUtility.addEventListenerMe();
        }
    }

    _onFormsPendingPatchesChanged() {
        const formValue = this._getFormValue();
        if (
            _.get(formValue, "connectionObject") &&
            _.get(formValue, "connectionObject.connectionType") &&
            !this._pristineConnectionObject
        ) {
            this._pristineConnectionObject = _.get(formValue, "connectionObject");
        }

        if (!this._renderingForm) {
            //const form = this._formsComponent.getValue(),
            const isDirty = !_.isEmpty(this._getFormValue());
            this._toggleActions();
            this._model.setTabDirty(isDirty);
            /*
            if (this._model._isOSMSolutionAvailable && form.fileServerId &&  this.model.getDevicesRelatedImageServer() !== form.fileServerId) {
                this._model._devices.forEach((device) => {
                    device.setImageServerRelationship(form.fileServerId);
                });
            }*/
        }
    }

    _onDevicePropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let devicePropsVal =
                this._model._deviceCategory === "iot"
                    ? this._getIotPristineValue()
                    : this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE

            let updateReadOnlyFields = () => {
                _.keys(devicePropsVal).map((id) => {
                    if (readOnlyFieldIds.indexOf(id) === -1) {
                        devicePropsVal[id] = formVal[id];
                    }
                });
                this._formsComponent.setPristineValue(devicePropsVal);
                return Promise.resolve();
            };

            if (this._model.isDirty()) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(
                            _.isEmpty(pristineProperties[id]) ? "" : pristineProperties[id],
                            _.isEmpty(before[id]) ? "" : before[id]
                        )
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });
                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This device has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onDeviceDetailsCancelAction =
                            this._onDeviceDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(devicePropsVal);
            }
        }
    }

    _onDirty() {
        this._toggleActions();
    }

    _onDeviceDetailsSave() {
        this._disableViewActions();
        // this._model._inheritedCustomFields = this._siteCfUtility.getInheritedCfs();
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => {
                const formValue = this._getFormValue();
                const deletePathsMap = this._buildDeletePathsMap();
                return this._model.saveDevice(
                    formValue,
                    this._pristineConnectionObject,
                    deletePathsMap
                );
            })
            .then(() => {
                return (this._pristineConnectionObject = undefined);
                // return this._hideModal();
            })
            .catch((err) => this._showError(err))
            .catch((err = {}) => (err.userRejected ? Promise.resolve() : Promise.reject(err)))
            .then(() => this._renderDeviceDetails())
            .catch((error) =>
                this._logger.warn({error}, "DeviceDetailsViewRender._onDeviceDetailsSave() error.")
            )
            .finally(() => {
                this._hideModal();
                if (_.isEmpty(this._model._devices)) {
                    this._model.closeTab();
                }
            });
    }

    _onDeviceDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onDeviceDetailsCancelAction = this._onDeviceDetailsCancelAction.bind(this))
        );
    }

    _onDeviceDetailsCancelAction() {
        this._siteCfUtility.clearInheritedCfs();
        return this._renderDeviceDetails();
    }

    _onOsImageServerFeedUpdated() {
        return this._renderDeviceDetails();
    }

    _onEngineZoneFeedUpdated() {
        return this._renderDeviceDetails();
    }

    _onOrgZoneFeedUpdated() {
        return this._renderDeviceDetails();
    }

    _onSiteFeedUpdated() {
        return this._renderDeviceDetails();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    _getPristineValues() {
        if (this._model._deviceCategory === "iot") {
            return this._model._devices.map(this._getIotPristineValue.bind(this));
        }
        return this._model._devices.map(this._getPristineValue.bind(this));
    }

    _getIotPristineValue(pristineProperties) {
        let pristineValue = {};

        if (pristineProperties) {
            // General
            pristineValue = {};
            pristineValue.name = pristineProperties.name;
            pristineValue.description = pristineProperties.description;
            pristineValue.environment = pristineProperties.environment;
            pristineValue.licenseCount = `${_.get(pristineProperties.licenseCount, "")}`;
            pristineValue.managementState = pristineProperties.managementState;

            // Site
            pristineValue.siteId = pristineProperties.siteId || "/";
            pristineValue.siteCodeName = pristineProperties.siteCodeName;
            pristineValue.allowSiteAutoAssignment = _.get(
                pristineProperties,
                "allowSiteAutoAssignment",
                true
            );

            // Discovery
            pristineValue.discoveryStatus = _.get(pristineProperties, "discoveryStatus");
            pristineValue.detectedVendor = `${_.get(pristineProperties, "detectedVendor")}`;
            pristineValue.detectedTypeBase = _.get(pristineProperties, "detectedTypeBase");
            pristineValue.detectedHostName = _.get(pristineProperties, "detectedHostName");
            pristineValue.osName = _.get(pristineProperties, "osName");
            pristineValue.osFamily = _.get(pristineProperties, "osFamily");
            pristineValue.osReleaseName = _.get(pristineProperties, "osReleaseName");
            pristineValue.osVersion = _.get(pristineProperties, "osVersion");
            pristineValue.machineArchitecture = _.get(pristineProperties, "machineArchitecture");
            pristineValue.language = _.get(pristineProperties, "language");
            pristineValue.locale = _.get(pristineProperties, "locale");
            pristineValue.detectedSerialNumber = _.get(pristineProperties, "detectedSerialNumber");
            pristineValue.detectedSku = `${_.get(pristineProperties, "detectedSku", "")}`;
            pristineValue.detectedUptime = _.get(pristineProperties, "detectedUptime")
                ? moment
                      .duration(_.get(pristineProperties, "detectedUptime"), "milliseconds")
                      .format("d:hh:mm:ss", {trim: false})
                : "";

            // Custom Fields
            pristineValue.customFields = _.get(pristineProperties, "customFieldValues");

            // OS Management
            pristineValue.osManagementActionBy = _.get(
                pristineProperties,
                "osManagementActionBy",
                ""
            );
            pristineValue.osManagementActionOn = this._toLocaleString(
                _.get(pristineProperties, "osManagementActionOn")
            );
            pristineValue.osManagementActionError = _.get(
                pristineProperties,
                "osManagementActionError",
                ""
            );
            pristineValue.osPlanName = _.get(pristineProperties, "osPlanName");
            pristineValue.osPlanAction = _.get(pristineProperties, "osPlanAction");

            // Activity
            pristineValue.lastActivityBy = _.get(pristineProperties, "lastActivityBy");
            pristineValue.lastActivityError = _.get(pristineProperties, "lastActivityError", "");
            pristineValue.lastActivityOn = this._toLocaleString(
                _.get(pristineProperties, "lastActivityOn")
            );
            pristineValue.lastActivityType = _.get(pristineProperties, "lastActivityType", "");
            pristineValue.discoveredBy = _.get(pristineProperties, "discoveredBy");
            pristineValue.discoveredOn = this._toLocaleString(
                _.get(pristineProperties, "discoveredOn")
            );
            pristineValue.rebootedBy = _.get(pristineProperties, "rebootedBy");
            pristineValue.rebootedOn = this._toLocaleString(
                _.get(pristineProperties, "rebootedOn", "")
            );
            pristineValue.updatedBy = pristineProperties.updatedBy;
            pristineValue.updatedOn = this._toLocaleString(pristineProperties.updatedOn);

            // CMDB
            pristineValue.lastCmdbSyncOn = this._toLocaleString(
                _.get(pristineProperties, "lastCmdbSyncOn")
            );

            // Extended Device Properties
            _.values(this._model._extendedPropList[pristineProperties.id]).forEach(
                (extendedProp) => {
                    Object.assign(pristineValue, {
                        [extendedProp.getExtensionType()]: extendedProp.getEdpInstance(),
                    });
                }
            );
        }

        return pristineValue;
    }

    _getPristineValue(device) {
        let pristineProperties = device,
            pristineValue = {},
            connectionInformation = device.connectionInformation || {
                "connectionType@Select.options": {
                    cliConnection: this._model._DeviceEntityDef.connectionInformationJtype,
                    "cliConnection@Display.label": " CLI connection",
                },
            };

        if (device) {
            pristineValue.connectionObject = {};
            if (this._model._lockedConnectionMethods.length === 1) {
                pristineValue.connectionObject.connectionType =
                    this._model._lockedConnectionMethods[0];
            } else {
                pristineValue.connectionObject.connectionType = _.get(
                    pristineProperties,
                    "connectionMethod",
                    "cliConnection"
                );
            }
            _.merge(
                pristineValue.connectionObject,
                _.get(pristineProperties, "connectionInformation")
            );
            pristineValue.connectionObject = _.omit(pristineValue.connectionObject, "ip");

            pristineValue.name = pristineProperties.name;
            pristineValue.description = pristineProperties.description;
            pristineValue.managementState = pristineProperties.managementState;
            pristineValue.environment = pristineProperties.environment || "PRODUCTION";

            pristineValue.siteId = _.get(pristineProperties, "siteId", "/");

            pristineValue.allowSiteAutoAssignment = _.get(
                pristineProperties,
                "allowSiteAutoAssignment",
                true
            );

            pristineValue.latestHaveIChangedLastChecked = this._toLocaleString(
                pristineProperties.latestHaveIChangedLastChecked
            );
            pristineValue.capturedBy = pristineProperties.capturedBy;
            pristineValue.latestHaveIChanged = pristineProperties.latestHaveIChanged;

            pristineValue.discoveredOn = this._toLocaleString(pristineProperties.discoveredOn);
            pristineValue.discoveryStatus = pristineProperties.discoveryStatus;
            pristineValue.discoveredBy = pristineProperties.discoveredBy;

            pristineValue.engineZoneId = pristineProperties.engineZoneId;

            pristineValue.detectedVendor = pristineProperties.detectedVendor;
            pristineValue.detectedTypeBase = pristineProperties.detectedTypeBase;
            pristineValue.detectedHostName = pristineProperties.detectedHostName;
            pristineValue.detectedOs = pristineProperties.detectedOs;
            pristineValue.detectedSoftwareVersion = pristineProperties.detectedSoftwareVersion;
            pristineValue.detectedSerialNumber = pristineProperties.detectedSerialNumber;
            pristineValue.detectedSku = pristineProperties.detectedSku;
            pristineValue.elementCount = pristineProperties.elementCount || "";

            const format = `${
                _.get(
                    moment.duration(pristineProperties.detectedUptime, "milliseconds"),
                    "_data.years",
                    0
                ) > 0
                    ? "Y[y]"
                    : ""
            } M[m] d[d] h[h] m[m]`;
            pristineValue.detectedUptime = pristineProperties.detectedUptime
                ? moment
                      .duration(pristineProperties.detectedUptime, "milliseconds")
                      .format(format, {trim: false})
                : "";

            if (this.model._isOSMSolutionAvailable === true) {
                pristineValue.fileServerId = this.model.getDevicesRelatedImageServer();
                pristineValue.vrf = pristineProperties.vrf;
                pristineValue.useIssu = pristineProperties.useIssu;
            }

            pristineValue.engineZoneId = pristineProperties.engineZoneId;
            pristineValue.lockEngine = pristineProperties.lockEngine;
            // TODO Check why updatedOn and updatedBy only available at top level
            pristineValue.updatedOn = this._toLocaleString(device.updatedOn);
            pristineValue.updatedBy = device.updatedBy;

            pristineValue.lastActivityOn = this._toLocaleString(pristineProperties.lastActivityOn);
            pristineValue.lastActivityBy = pristineProperties.lastActivityBy;
            pristineValue.lastActivityType = pristineProperties.lastActivityType;
            pristineValue.lastActivityError = pristineProperties.lastActivityError;
            pristineValue.discoveryLevel = pristineProperties.discoveryLevel;

            if (pristineProperties.customFieldValues) {
                pristineValue.customFieldValues = {};
                let customFieldValues = pristineProperties.customFieldValues;
                Object.keys(customFieldValues).forEach((key) => {
                    if (
                        this._model._customFieldColumnDef[key] &&
                        this._model._customFieldColumnDef[key].enabled &&
                        (this._model._customFieldColumnDef[key].systemOwned !== true ||
                            this._model.gameEditor.isCiscoSupportEnabled)
                    ) {
                        pristineValue.customFieldValues[key] = customFieldValues[key];
                    }
                });
            }
            if (this._model._devicesSupportedForBackup) {
                pristineValue.restoreCommands = pristineProperties.restoreCommands;
            }
            // connectionInformation = _.omit(connectionInformation, "ip")
            _.merge(pristineValue, connectionInformation);

            // Extended Device Properties
            _.values(this._model._extendedPropList[pristineProperties.id]).forEach(
                (extendedProp) => {
                    Object.assign(pristineValue, {
                        [extendedProp.getExtensionType()]: extendedProp.getEdpInstance(),
                    });
                }
            );
        }

        return pristineValue;
    }

    _setReadOnly() {
        const formElem = document.querySelector(".form-section");

        if (formElem) {
            if (this._model._controlledDevicesPresent || this._model._multipleConnectionMethods) {
                const connectionObject = formElem.querySelector(".connection_object");
                if (connectionObject) {
                    connectionObject.classList.add("disabled");
                }
            }

            if (this._model._lockedConnectionMethods.length > 0) {
                const connectionMethod = formElem.querySelector(".connection_method");
                if (connectionMethod) {
                    connectionMethod.classList.add("disabled");
                }
            }

            if (this._model._controlledDevicesPresent) {
                const engineZoneId = formElem.querySelector(".engine_zone_id");
                if (engineZoneId) {
                    engineZoneId.classList.add("disabled");
                }

                const lockEngine = formElem.querySelector(".lock_engine");
                if (lockEngine) {
                    lockEngine.classList.add("disabled");
                }

                const fileServerId = formElem.querySelector(".file_server_id");
                if (fileServerId) {
                    fileServerId.classList.add("disabled");
                }

                const vrf = formElem.querySelector(".vrf");
                if (vrf) {
                    vrf.classList.add("disabled");
                }

                const useIssu = formElem.querySelector(".use_issu");
                if (useIssu) {
                    useIssu.classList.add("disabled");
                }
            }
        }
    }

    _getIotJtype() {
        let jtype = {};
        let customFields = this._model._customFieldColumnDef;

        jtype.name = "";
        jtype["name@Display.label"] = "Name";

        jtype.description = "";
        jtype["description@Display.label"] = "Description";

        // Multi-select chips via array_component.control_adapter.js (@Display.tagList).
        jtype.tagIds = this.model.tagOptions.map((tag) => _.keys(tag)[0]);
        jtype["tagIds@Display.label"] = "Tags";
        jtype["tagIds@Display.tagList.labels"] = this.model.tagOptions.map(
            (tag) => _.values(tag)[0]
        );
        // New option ids are prefixed "__new__" (see MultipleComboBoxControlComponent.createOption).
        jtype["tagIds@Display.tagList.createOption"] = this.model._canCreateTagsOnTheFly;

        jtype["managementState@Enum"] = ["MANAGED", "UNMANAGED", "INVENTORY_ONLY"];
        jtype.managementState = "MANAGED";
        jtype["managementState@Display.label"] = "Management State";
        jtype["managementState@Display.enum"] = ["Managed", "Unmanaged", "Inventory Only"];
        jtype["managementState@Required"] = true;

        jtype["environment@Enum"] = ["PRODUCTION", "TEST", "PRODUCTION-TEST"];
        jtype.environment = "PRODUCTION";
        jtype["environment@Display.label"] = "Environment";
        jtype["environment@Display.enum"] = ["Production", "Test", "Production/Test"];
        jtype["environment@Required"] = true;

        jtype["siteId@Enum"] = this.model.siteIds;
        jtype["siteId@Display.enum"] = this.model.sitePaths;
        jtype.siteId = "/";
        jtype["siteId@Display.label"] = "Site Path";
        jtype["siteId@Required"] = true;

        jtype["allowSiteAutoAssignment@Display.label"] = "Allow site auto-assignment";
        jtype.allowSiteAutoAssignment = true;

        jtype.discoveryStatus = "";
        jtype["discoveryStatus@Display.label"] = "Discovered Status";

        jtype.detectedVendor = "";
        jtype["detectedVendor@Display.label"] = "Vendor";

        jtype.detectedTypeBase = "unknown";

        const typeBaseVals = Object.values(
            Object.fromEntries(
                Object.entries(this._model._DeviceEntityDef.getTypeBaseMap()).filter(
                    ([key]) =>
                        this._model._DeviceEntityDef.getTypeBaseToCategoryMap()[key] === "iot"
                )
            )
        )
            .filter((val) => val !== "Unknown")
            .sort();

        jtype["detectedTypeBase@Enum"] = typeBaseVals.concat("unknown");
        jtype["detectedTypeBase@Display.enum"] = typeBaseVals.concat("Unknown");
        jtype["detectedTypeBase@Display.label"] = "Type";
        jtype["detectedTypeBase@Required"] = true;

        jtype.detectedHostName = "";
        jtype["detectedHostName@Display.label"] = "Hostname";

        jtype.osName = "";
        jtype["osName@Display.label"] = "OS Name";

        jtype.osFamily = "";
        jtype["osFamily@Display.label"] = "OS Family";

        jtype.osReleaseName = "";
        jtype["osReleaseName@Display.label"] = "OS Release Name";

        jtype.osVersion = "";
        jtype["osVersion@Display.label"] = "OS Version";

        jtype.machineArchitecture = "";
        jtype["machineArchitecture@Display.label"] = "Machine Architecture";

        jtype.language = "";
        jtype["language@Display.label"] = "OS Language";

        jtype.locale = "";
        jtype["locale@Display.label"] = "Locale";

        jtype.detectedSerialNumber = "";
        jtype["detectedSerialNumber@Display.label"] = "Serial";

        jtype.detectedUptime = "";
        jtype["detectedUptime@Display.label"] = "Up Time";

        if (customFields) {
            //const customFieldsJtypeSection = this.model.customFieldsJtypes;
            //Object.assign(jtype, ...customFieldsJtypeSection);

            jtype.customFieldValues = {};
            jtype["customFieldValues@Display.label"] = "Custom Fields";
            jtype["customFieldValues@Display.readOnly"] = true;
            const customFieldValues = this.model.customFieldsJtypes;
            Object.assign(jtype.customFieldValues, ...customFieldValues);
        }

        jtype.osManagementActionBy = "";
        jtype["osManagementActionBy@Display.label"] = "OSM Action By";

        jtype.osManagementActionOn = "";
        jtype["osManagementActionOn@Display.label"] = "OSM Action On";

        jtype.osManagementActionError = "";
        jtype["osManagementActionError@Display.label"] = "OSM Action Error";

        jtype.osPlanName = "";
        jtype["osPlanName@Display.label"] = "OS Plan Name";

        jtype.osPlanAction = "";
        jtype["osPlanAction@Display.label"] = "OS Plan Action";

        jtype.lastActivityType = "";
        jtype["lastActivityType@Display.label"] = "Last Activity Type";

        jtype.lastActivityOn = "";
        jtype["lastActivityOn@Display.label"] = "Last Activity On";

        jtype.lastActivityBy = "";
        jtype["lastActivityBy@Display.label"] = "Last Activity By";

        jtype.lastActivityError = "";
        jtype["lastActivityError@Display.label"] = "Last Activity Error";

        jtype.updatedOn = "";
        jtype["updatedOn@Display.label"] = "Updated On";

        jtype.updatedBy = "";
        jtype["updatedBy@Display.label"] = "Updated By";

        jtype.lastCmdbSyncOn = "";
        jtype["lastCmdbSyncOn@Display.label"] = "CMDB Sync On";

        // EDP
        this._model._extendedPropTypes.forEach((extendedProp) => {
            jtype = Object.assign(jtype, this.processExtendedPropTabs(extendedProp));
        });

        //append readOnly props
        readOnlyFieldIds.forEach((readOnlyField) => {
            if (_.has(jtype, readOnlyField)) {
                jtype[`${readOnlyField}@Display.readOnly`] = true;
            }
        });

        return jtype;
    }

    _getJtype() {
        if (this._model._deviceCategory === "iot") {
            return this._getIotJtype();
        }

        let jtype = {};
        let customFields = this._model._customFieldColumnDef;

        jtype.name = "";
        jtype["name@Display.label"] = "Name";

        jtype.description = "";
        jtype["description@Display.label"] = "Description";

        // Multi-select chips via array_component.control_adapter.js (@Display.tagList).
        jtype.tagIds = this.model.tagOptions.map((tag) => _.keys(tag)[0]);
        jtype["tagIds@Display.label"] = "Tags";
        jtype["tagIds@Display.tagList.labels"] = this.model.tagOptions.map(
            (tag) => _.values(tag)[0]
        );
        // New option ids are prefixed "__new__" (see MultipleComboBoxControlComponent.createOption).
        jtype["tagIds@Display.tagList.createOption"] = this.model._canCreateTagsOnTheFly;

        jtype["siteId@Enum"] = this.model.siteIds;
        jtype["siteId@Display.enum"] = this.model.sitePaths;
        jtype.siteId = "/";
        jtype["siteId@Display.label"] = "Site Path";
        jtype["siteId@Required"] = true;

        jtype["allowSiteAutoAssignment@Display.label"] = "Allow site auto-assignment";
        jtype.allowSiteAutoAssignment = true;

        jtype["discoveryLevel@Enum"] = Object.values(
            this._model._DeviceEntityDef.getDiscoveryLevelValues()
        ).map(Number); //must convert to number, Object.keys won't auto convert either

        jtype["discoveryLevel@Display.enum"] = Object.values(
            this._model._DeviceEntityDef.getDiscoveryLevelLabels()
        );
        jtype.discoveryLevel = "";
        jtype["discoveryLevel@Display.label"] = "Discovery Level";
        jtype["discoveryLevel@Required"] = true;

        jtype["managementState@Enum"] = ["MANAGED", "UNMANAGED", "INVENTORY_ONLY"];
        jtype.managementState = "MANAGED";
        jtype["managementState@Display.label"] = "Management State";
        jtype["managementState@Display.enum"] = ["Managed", "Unmanaged", "Inventory Only"];
        jtype["managementState@Required"] = true;

        jtype["environment@Enum"] = ["PRODUCTION", "TEST", "PRODUCTION-TEST"];
        jtype.environment = "PRODUCTION";
        jtype["environment@Display.label"] = "Environment";
        jtype["environment@Display.enum"] = ["Production", "Test", "Production/Test"];
        jtype["environment@Required"] = true;

        // _.merge(jtype, this._model.getConnectionInfoJtype(), {
        //     "type@Required": true
        // });

        const connectionObjectValue = {
            connectionObject: {
                "connectionType@Display.label": "Connection Method",
                "connectionType@Required": true,
            },
        };

        Object.assign(connectionObjectValue.connectionObject, this._model._connectionDropdownList);
        Object.assign(jtype, this._model._connectionTypeDefs);

        _.merge(jtype, connectionObjectValue);

        if (customFields) {
            //const customFieldsJtypeSection = this.model.customFieldsJtypes;
            //Object.assign(jtype, ...customFieldsJtypeSection);

            jtype.customFieldValues = {};
            jtype["customFieldValues@Display.label"] = "Custom Fields";
            jtype["customFieldValues@Display.readOnly"] = true;
            const customFieldValues = this.model.customFieldsJtypes;
            Object.assign(jtype.customFieldValues, ...customFieldValues);
        }

        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        switch (this._model._solutionName) {
            case "config_drift":
                jtype.latestHaveIChangedLastChecked = "";
                jtype["latestHaveIChangedLastChecked@Display.label"] = "Captured On";
                jtype.latestHaveIChanged = "";
                jtype["latestHaveIChanged@Display.label"] = "Captured Status";
                jtype.capturedBy = "";
                jtype["capturedBy@Display.label"] = "Captured By";
                break;
            case "device_manager":
                jtype.discoveredOn = "";
                jtype["discoveredOn@Display.label"] = "Discovered On";
                jtype.discoveryStatus = "";
                jtype["discoveryStatus@Display.label"] = "Discovered Status";
                jtype.discoveredBy = "";
                jtype["discoveredBy@Display.label"] = "Discovered By";
                break;
        }

        jtype.detectedVendor = "";
        jtype["detectedVendor@Display.label"] = "Vendor";

        const typeBaseVals = Object.values(
            Object.fromEntries(
                Object.entries(this._model._DeviceEntityDef.getTypeBaseMap()).filter(
                    ([key]) =>
                        this._model._DeviceEntityDef.getTypeBaseToCategoryMap()[key] === "network"
                )
            )
        )
            .filter((val) => val !== "Unknown")
            .sort();

        jtype.detectedTypeBase = "unknown";
        jtype["detectedTypeBase@Enum"] = typeBaseVals.concat("unknown");
        jtype["detectedTypeBase@Display.enum"] = typeBaseVals.concat("Unknown");
        jtype["detectedTypeBase@Display.label"] = "Type";
        jtype["detectedTypeBase@Required"] = true;

        jtype.detectedHostName = "";
        jtype["detectedHostName@Display.label"] = "Hostname";

        jtype.detectedOs = "";
        jtype["detectedOs@Display.label"] = "OS";

        jtype.detectedSoftwareVersion = "";
        jtype["detectedSoftwareVersion@Display.label"] = "OS Version";
        jtype.detectedSerialNumber = "";
        jtype["detectedSerialNumber@Display.label"] = "Serial";
        jtype.detectedSku = "";
        jtype["detectedSku@Display.label"] = "SKUs";
        jtype.detectedUptime = "";
        jtype["detectedUptime@Display.label"] = "Up Time";
        jtype.elementCount = "";
        jtype["elementCount@Display.label"] = "Elements";

        if (!_.isEmpty(this.model.visibleEngineZoneList)) {
            jtype["engineZoneId@Enum"] = this.model.engineZoneIds;
            jtype["engineZoneId@Display.enum"] = this.model.zoneNames;
            jtype["engineZoneId@Display.label"] = "Zone";
            jtype["lockEngine"] = false;
            jtype["lockEngine@Display.label"] = "Lock Zone";
        }

        if (this.model._isOSMSolutionAvailable === true) {
            jtype["fileServerId@Enum"] = this.model.osImageServerIds;
            jtype["fileServerId@Display.enum"] = this.model.osImageServerNames;
            jtype["fileServerId@Display.label"] = "File Server";
            jtype.fileServerId = null;
            jtype.vrf = "";
            jtype["vrf@Display.label"] = "VRF";
            jtype.useIssu = false;
            jtype["useIssu@Display.label"] = "Non-disruptive";
        }
        if (this._model._devicesSupportedForBackup) {
            jtype.restoreCommands = "";
            jtype["restoreCommands@Display.label"] = "Post Restore Commands";
            jtype["restoreCommands@Display.editor"] = {
                minLineCount: 4,
                maxLineCount: 4,
            };
        }

        //config meta data
        jtype.configuredInfo = "";
        jtype["configuredInfo@Display.label"] = "Configure Info";

        jtype.configuredType = "";
        jtype["configuredType@Display.label"] = "Configure Type";

        jtype.configuredOn = "";
        jtype["configuredOn@Display.label"] = "Configured On";

        jtype.configuredBy = "";
        jtype["configuredBy@Display.label"] = "Configured By";

        jtype.updatedOn = "";
        jtype["updatedOn@Display.label"] = "Updated On";
        jtype.updatedBy = "";
        jtype["updatedBy@Display.label"] = "Updated By";

        jtype.lastActivityOn = "";
        jtype["lastActivityOn@Display.label"] = "Last Activity On";
        jtype.lastActivityBy = "";
        jtype["lastActivityBy@Display.label"] = "Last Activity By";
        jtype.lastActivityType = "";
        jtype["lastActivityType@Display.label"] = "Last Activity Type";
        jtype.lastActivityError = "";
        jtype["lastActivityError@Display.label"] = "Last Activity Error";

        // EDP
        this._model._extendedPropTypes.forEach((extendedProp) => {
            jtype = Object.assign(jtype, this.processExtendedPropTabs(extendedProp));
        });

        //append readOnly props
        readOnlyFieldIds.forEach((readOnlyField) => {
            if (_.has(jtype, readOnlyField)) {
                jtype[`${readOnlyField}@Display.readOnly`] = true;
            }
        });

        return jtype;
    }

    _getFormValue() {
        return this._formsComponent.getFormValue();
    }

    _getNonSelectedPaths(extensionType) {
        if (this._formsComponent && this._formsComponent.getNonSelectedPaths) {
            const prefix = extensionType + ".";
            return this._formsComponent
                .getNonSelectedPaths("")
                .filter(function (p) {
                    return p.indexOf(prefix) === 0;
                })
                .map(function (p) {
                    return p.substring(prefix.length);
                });
        }
        return [];
    }

    _buildDeletePathsMap() {
        const map = {};
        this._model._extendedPropTypes.forEach((extendedPropType) => {
            const extensionType = extendedPropType.getExtensionType();
            const paths = this._getNonSelectedPaths(extensionType);
            if (paths.length > 0) {
                map[extensionType] = paths;
            }
        });
        return map;
    }

    processExtendedPropTabs(extendedProp) {
        const extensionType = extendedProp.getExtensionType();
        const extendedPropTypeJType = extendedProp.getJtype();
        // Create a deep copy to avoid modifying the input object
        const result = JSON.parse(JSON.stringify(extendedPropTypeJType));

        // Find all keys ending with @Display.tab
        const tabKeys = Object.keys(result).filter((key) => key.endsWith("@Display.tab"));

        let readOnlyCount = 0;

        tabKeys.forEach((tabKey) => {
            // Extract tab name (e.g., deviceIdDetails from deviceIdDetails@Display.tab)
            const tabName = tabKey.split("@")[0];

            // Get the corresponding sub-object
            const tabObject = result[tabName];
            if (!tabObject || typeof tabObject !== "object") {
                return;
            }

            // Get all fields in the tab (exclude metadata keys like @PropVisibility or @Display)
            const fields = Object.keys(tabObject).filter(
                (key) => !key.includes("@") && typeof tabObject[key] !== "object"
            );

            // Check if all fields have @Display.readOnly: true
            const allReadOnly = fields.every(
                (field) => tabObject[`${field}@Display.readOnly`] === true
            );

            // If all fields are read-only, add <tabName>@Display.readOnly: true
            if (allReadOnly && fields.length > 0) {
                result[`${tabName}@Display.hidden`] = true;
                readOnlyCount++;
            }
        });

        // hide entire result  if all sub-tabs are hidden
        if (tabKeys.length > 0 && readOnlyCount === tabKeys.length) {
            return {
                [extensionType]: result,
                [`${extensionType}@Display.label`]: extendedProp.getDisplayName(),
                [`${extensionType}@Display.hidden`]: true,
            };
        }

        return {
            [extensionType]: result,
            [`${extensionType}@Display.label`]: extendedProp.getDisplayName(),
        };
    }
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = DeviceMultiEditViewRenderer;
